/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
/**
 * @file
 */

import uuidv4 from 'uuid/v4';
// eslint-disable-next-line import/no-cycle
import { providers } from './Providers';
import { auth2 } from './Auth2API';
import { analytics } from './Analytics';
import { getEnvVar, getHostEnv } from '../core/EnvUtil';
import { logging } from './LoggingAPI';
import { router } from './RouterAPI';
import { fetchWithTimeout } from '../core/CommonUtil';

const logger = logging.getLogger('SophiaProvider');
const DEFAULT_API_KEY = 'dc-local-virgoweb';
const IMS_CLIENT_ID_KEY = 'ims_client_id';
const SOPHIA_CONTENT_URI_KEY = 'sophia_content_uri';
const X_ADOBE_UUID = 'x-adobe-uuid';

const getClientId = () => getEnvVar(IMS_CLIENT_ID_KEY);

/**
 * @classdesc
 * Sophia Provider to manage Sophia
 * @class
 */
class SophiaProviderImpl {
  /**
   * @constructor
   * @param {Object} obj
   * @param {AuthAPI} obj.auth - The instance of AuthAPI for testing.
   * @param {Array} obj.surfaceIds - List of all the surfaces data that needs to be fetched
   * @param {String} obj.sophiaUri - uri to make sophia API calls
   * @param {String} obj.imsClientId - ims client id registered with sophia
   * @param {String} obj.imsToken - The instance of AuthAPI for testing.
   * @param {String} obj.useAnonymousUUID - Use userUUID to fetch sophia surface data for anonymous users.
   * @param {Object} obj.context - Key/value pairs to be passed as context to sophia
   * @param {Promise} obj.sophiaResponse - Promise that resolves to sophia data.
   * Default value is false.
   */
  constructor(obj = {}) {
    this.surfaceIds = obj.surfaceIds || [];
    this.auth = obj.auth || auth2;
    this.clientId = obj.imsClientId || getClientId();
    this.context = obj.context || {};
    this.uri = this.getApiUrl(obj.sophiaUri);
    this.useAnonymousUUID = obj.useAnonymousUUID || false;
    this.accessToken = this.getAccessToken(obj);
    this.timeoutMS = 5 * 1000;
    this.surfaceData = {};

    this.clientSophiaResponse = obj.sophiaResponse;

    this.auth.signInPromise.then(() => {
      // if already made Sophia call as signed in user, return
      if (this.madeAuthenticatedCall) {
        return;
      }
      // Invalidate ready. Any calls to this provider from this point on will wait for current surface data from sophia.
      this.readyPromise = null;

      // Get the latest token. Learn more on how the imslibjs works - https://git.corp.adobe.com/pages/IMS/imslib.js/
      const newAccessToken = this.getAccessToken(obj);

      logger.emit('Sophia update', {
        level: 'debug',
        signed_in: auth2.isSignedIn,
        old_auth: this.accessToken && this.accessToken.slice(0, 20),
        new_auth: newAccessToken.slice(0, 20),
      });

      this.accessToken = newAccessToken;
      // Reset providers.sophia entry so that they can get new readyPromise
      providers.sophia = () => this.ready();
    });
  }

  getAccessToken = obj => obj.imsToken || (this.auth.getAccessTokenInfo() || {}).access_token;

  /**
   * @description
   * Standard provider ready() method to allow lazy instantiation of API.
   * @method
   * @returns {Promise} - promise that resolves when SophiaProvider provider has been instantiated
   */
  ready() {
    if (!this.readyPromise) {
      this.readyPromise = this.callSophiaAPIInit()
        .then(result => {
          this.parseSurfaceData(result);
          return this;
        });
    }
    return this.readyPromise;
  }

  /**
   * @description
   * Standard provider refreshReady() method to allow lazy instantiation of API.
   * @method
   * @returns {Promise} - promise that resolves when SophiaProvider provider has been instantiated
   */
  refreshReady() {
    if (!this.readyPromise) {
      this.readyPromise = this.callSophiaAPIInit()
        .then(result => {
          this.parseSurfaceData(result);
          return this;
        });
    }
    return this.readyPromise;
  }

  /**
   * @method
   * @param {String} baseUri - baseUri for the sophia API passed from the constructor
   * @returns {String} - Sophia API uri
   * @public
   */
  getApiUrl(baseUri, surfaceId, context) {
    const clientId = this.clientId;
    const uri = baseUri || getEnvVar(SOPHIA_CONTENT_URI_KEY);
    if (!uri || !clientId) {
      throw new Error(`missing ${SOPHIA_CONTENT_URI_KEY} or ${IMS_CLIENT_ID_KEY}`);
    }

    if (this.surfaceIds.length < 1 && !surfaceId) {
      throw new Error('Missing SurfaceId');
    }

    const urlWithParams = new URL(`${uri}/v2/content`);

    let localContext;
    if (surfaceId) {
      urlWithParams.searchParams.append('surfaceId', surfaceId);
      localContext = context;
    } else {
      this.surfaceIds.forEach(id => {
        urlWithParams.searchParams.append('surfaceId', id);
      });
      localContext = this.context;
    }

    if (localContext) {
      Object.keys(localContext).forEach(key => {
        // eslint-disable-nextline
        urlWithParams.searchParams.append(key, localContext[key]);
      });
    }

    return urlWithParams.href;
  }

  /**
   * @method
   * @returns {string} - API key
   * @public
   */
  get apiKey() {
    return this.clientId || DEFAULT_API_KEY;
  }

  /**
   * @method
   * @param {object} response - Object containing various surface data with containers
   * @returns {object} surfaceData - List of surfaces with container information
   * @public
   */
  parseSurfaceData(response) {
    if (response) {
      this.surfaceData = response.surfaces;
    }
  }

  /**
   * Returns surface data for a specific surface Id
   * If the surfaceId is not provided, returns all the available surface data instantiated
   * @param {string} surfaceId - The Surface ID whose presence is to be checked.
   * @param {boolean} signedInCheck - The signed in flag boolean.
   */
  getSurfaceData = async (surfaceId = '', signedInCheck = true) => {
    if (signedInCheck) {
      await this.isImsReadySet();
      await this.refreshReady();
    }
    return surfaceId ? this.surfaceData[surfaceId] : this.surfaceData;
  };

  getSurfaceDataFromAPI = async (surfaceId, context) => {
    let response;
    if (surfaceId) {
      const uri = this.getApiUrl(null, surfaceId, context);
      const result = await this.callSophiaAPI(uri);
      response = result.surfaces[surfaceId];
    }
    return response;
  };

  /**
   *
   * @param {string} surfaceId - The Surface ID whose presence is to be checked.
   * @param {boolean} signedInCheck - The signed in flag boolean.
   * @returns {boolean} - Result a promise with a value of true/false.
   */
  hasSurface = async (surfaceId, signedInCheck = true) => {
    if (signedInCheck) {
      await this.isImsReadySet();
      await this.refreshReady();
    }
    // eslint-disable-next-line
    return this.surfaceData.hasOwnProperty(surfaceId);
  };

  /**
   * @returns {boolean} - Result is a value true/false
   */
  isImsReadySet() {
    return window.adobeIMS && window.adobe_dc_sdk.loaded && window.adobe_dc_sdk.loaded.imsReady;
  }

  createAnonUserUUID() {
    const id = `${getHostEnv()}_${this.clientId}_${uuidv4()}`;
    try {
      localStorage.setItem('anonUserUUID', id);
    } catch (e) {
      // squelch if local storage is not available
      logger.warn('Cannot access localStorage', e);
    }
    return id;
  }

  /**
   * @method
   * @public
   */
  async callSophiaAPIInit() {
    if (this.clientSophiaResponse) {
      return this.clientSophiaResponse;
    }
    return this.callSophiaAPI(this.uri);
  }

  /**
   * @method
   * @public
   */
  async callSophiaAPI(uri) {
    const authentication = this.accessToken ? `Bearer ${this.accessToken}` : '';
    const fetchHeaders = {
      'x-api-key': this.apiKey,
    };

    const makeSophiaAPICall = () => {
      let res = {};
      return fetchWithTimeout(uri, {
        method: 'GET',
        headers: fetchHeaders,
      })
        .then(response => {
          res = response;
          const contentType = response.headers.get('content-type');
          if (response.ok && contentType && contentType.includes('application/json')) {
            // reset the error, just in case someone reset the readyPromise
            this.error = undefined;
            // json() actually returns a Promise, so dereference again to the actual result.
            return response.json();
          }
          // We need to deference the text() Promise to get any error detail.
          return response.text().then(result => {
            logger.emit('Sophia failure', {
              level: 'warn',
              auth: authentication.slice(0, 20),
              code: res.status,
              status_text: res.statusText,
              err: result,
            });
            this.error = new Error(`Sophia failure: ${result}`);
          });
        })
        .catch(err => {
          logger.emit('Sophia failure', {
            level: 'warn',
            auth: authentication.slice(0, 20),
            code: res.status,
            status_text: res.statusText,
            err,
          });
          this.error = err && err.message ? err : new Error(`Sophia failure: ${JSON.stringify(err)}`);
        });
    };

    // Always use the uuid for ensuring stickiness in case of unath, or unath -> auth scenario
    if (this.useAnonymousUUID) {
      const visitorId = await analytics.getECID();
      if (visitorId) {
        fetchHeaders['x-adobe-uuid-type'] = 'visitorId';
        fetchHeaders[X_ADOBE_UUID] = visitorId;
      } else {
        // x-adobe-uuid-type is required if x-adobe-uuid is present. Supported values are deviceId, sessionId, visitorId
        fetchHeaders['x-adobe-uuid-type'] = 'sessionId';

        const queryParams = router.getQueryParams();

        // The router will have query param when coming from other origins like acrobat.com
        try {
          fetchHeaders[X_ADOBE_UUID] = queryParams.anonUserUUID || localStorage.getItem('anonUserUUID');
        } catch (e) {
          // squelch if local storage is not available
          logger.warn('Cannot access localStorage', e);
        }
        if (!fetchHeaders[X_ADOBE_UUID]) {
          if (authentication) {
            fetchHeaders.Authorization = authentication;
          } else {
            fetchHeaders[X_ADOBE_UUID] = this.createAnonUserUUID();
          }
        }
      }
    } else if (authentication) {
      this.madeAuthenticatedCall = true;
      fetchHeaders.Authorization = authentication;
      return providers.limits2().then(limits2 => {
        if (limits2.isPrivilegedAction('sophia-integration')) {
          logger.emit('Sophia failure', {
            level: 'warn',
            auth: authentication.slice(0, 20),
            reason: 'Privileged action detected. Ignoring Sophia call.',
          });
        } else {
          return makeSophiaAPICall();
        }
      });
    }
    return makeSophiaAPICall();
  }
}

export default SophiaProviderImpl;
